import React, { useCallback, useEffect, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { ModalHeader } from '../../components/ModalHeader';
import { Icon } from '../../components/Icon';
import { useToast } from '../../hooks/toast';
import { api } from '../../services/api';
import { colors } from '../../styles/tokens';

import {
  Container,
  Content,
  EmptyState,
  Item,
  ItemBody,
  ItemContent,
  ItemDate,
  ItemDot,
  ItemTitle,
  List,
  LoadMoreButton,
  MarkAllButton,
  MarkAllRow,
} from './styles';

interface INotification {
  id: string;
  title: string;
  body: string;
  read_at: string | null;
  created_at: string;
}

const NotificationsPanel: React.FC = () => {
  const { addToast } = useToast();

  const [notifications, setNotifications] = useState<INotification[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchPage = useCallback(
    (targetPage: number) =>
      api
        .get('notifications', {
          params: { app: 'staff', page: targetPage, limit: 20 },
        })
        .then(response => {
          setTotalPages(response.data.totalPages);
          setUnreadCount(response.data.unreadCount);
          setNotifications(current =>
            targetPage === 1
              ? response.data.data
              : [...current, ...response.data.data],
          );
          setPage(targetPage);
        })
        .catch(() =>
          addToast({
            type: 'error',
            title: 'Erro ao buscar notificações.',
          }),
        ),
    [addToast],
  );

  useEffect(() => {
    setLoading(true);
    fetchPage(1).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoadMore = useCallback(() => {
    setLoadingMore(true);
    fetchPage(page + 1).finally(() => setLoadingMore(false));
  }, [fetchPage, page]);

  const handleItemClick = useCallback(
    (notification: INotification) => {
      if (notification.read_at) {
        return;
      }

      setNotifications(current =>
        current.map(item =>
          item.id === notification.id
            ? { ...item, read_at: new Date().toISOString() }
            : item,
        ),
      );
      setUnreadCount(current => Math.max(0, current - 1));

      api.patch(`notifications/${notification.id}/read`).catch(() =>
        addToast({
          type: 'error',
          title: 'Erro ao marcar notificação como lida.',
        }),
      );
    },
    [addToast],
  );

  const handleMarkAllRead = useCallback(() => {
    setNotifications(current =>
      current.map(item => ({
        ...item,
        read_at: item.read_at || new Date().toISOString(),
      })),
    );
    setUnreadCount(0);

    api
      .patch('notifications/read-all', null, { params: { app: 'staff' } })
      .catch(() =>
        addToast({
          type: 'error',
          title: 'Erro ao marcar notificações como lidas.',
        }),
      );
  }, [addToast]);

  return (
    <Container>
      <ModalHeader
        title="Notificações"
        subtitle={unreadCount ? `${unreadCount} não lida(s)` : 'Tudo em dia'}
      />

      <Content>
        {!!unreadCount && !loading && (
          <MarkAllRow>
            <MarkAllButton type="button" onClick={handleMarkAllRead}>
              Marcar todas como lidas
            </MarkAllButton>
          </MarkAllRow>
        )}

        {!loading && notifications.length > 0 && (
          <List>
            {notifications.map(notification => (
              <Item
                key={notification.id}
                type="button"
                unread={!notification.read_at}
                onClick={() => handleItemClick(notification)}
              >
                <ItemDot unread={!notification.read_at} />
                <ItemContent>
                  <ItemTitle>{notification.title}</ItemTitle>
                  <ItemBody>{notification.body}</ItemBody>
                  <ItemDate>
                    há{' '}
                    {formatDistanceToNow(new Date(notification.created_at), {
                      locale: ptBR,
                    })}
                  </ItemDate>
                </ItemContent>
              </Item>
            ))}
          </List>
        )}

        {!loading && page < totalPages && (
          <LoadMoreButton
            type="button"
            onClick={handleLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? 'Carregando...' : 'Carregar mais'}
          </LoadMoreButton>
        )}

        {!loading && notifications.length === 0 && (
          <EmptyState>
            <Icon
              name="notifications"
              size={28}
              color={colors.onSurfaceVariant}
            />
            <span>Nenhuma notificação por aqui ainda.</span>
          </EmptyState>
        )}
      </Content>
    </Container>
  );
};

export { NotificationsPanel };
